import type { ConsistencyDay, Timestamp } from '@anytracker/core'
import { useEffect, useRef } from 'react'

import { ConsistencyStrip } from '../../components/ConsistencyStrip.js'
import { formatTime } from '../../format/dates.js'
import styles from './SavedConfirmation.module.css'

interface Props {
  readonly checkInName: string
  readonly count: number
  readonly at: Timestamp
  /** Consecutive logged days ending today, from core. */
  readonly streak: number
  /** The strip, most recent last. From core's `consistency` — the same
      function and the same bands Home uses, so the two cannot disagree about
      the same fortnight. */
  readonly days: readonly ConsistencyDay[]
  readonly onSeeChart: (() => void) | null
  readonly onHome: () => void
}

/**
 * The moment after saving (T-809).
 *
 * Doing two jobs. The obvious one is closing the loop on an action that
 * otherwise ends with a screen vanishing. The less obvious one is that this is
 * the **only** place the app connects logging to the reason for logging: "See
 * it on the chart" takes someone from the thing they just typed to the thing it
 * is for.
 *
 * The risk is that it becomes a toll booth — something that appears every time,
 * adds a tap, and is dismissed unread by week two. Two things hold that off and
 * both are load-bearing: **leaving takes no decision** (either button goes
 * somewhere, and Escape or back dismisses it), and one of the two goes
 * somewhere genuinely useful rather than back where you came from.
 *
 * If most people press "Back to home" every time, this has become a tax on
 * logging, and the honest fix is to make it conditional rather than prettier.
 */
export function SavedConfirmation({
  checkInName,
  count,
  at,
  streak,
  days,
  onSeeChart,
  onHome,
}: Props) {
  const heading = useRef<HTMLHeadingElement>(null)

  /*
   * Focus lands on the heading, not on a button.
   *
   * The control that was pressed to get here no longer exists, so focus would
   * otherwise fall to `<body>` — leaving a keyboard user at the top of a page
   * they cannot see the shape of. The heading is the sentence they want read.
   */
  useEffect(() => {
    heading.current?.focus()
  }, [])

  /*
   * Two days is the floor.
   *
   * A "streak" of one is just today, and calling it a streak is the kind of
   * flattery that makes the next one worth less. The strip disappears with it,
   * because a row of dots with one lit says nothing the sentence did not.
   */
  const showStreak = streak >= 2

  return (
    // A live region, because nothing here takes focus by force: the whole
    // screen changed and a screen reader should hear what it now says.
    <div className={`${styles['screen']} rise`} role="status" aria-live="polite">
      <div className={`${styles['badge']} pop`} aria-hidden="true">
        <svg viewBox="0 0 24 24" width="46" height="46" fill="none">
          <path
            d="M5 13l4 4 10-10"
            stroke="currentColor"
            strokeWidth="2.25"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </div>

      <div>
        <h1 className={styles['title']} ref={heading} tabIndex={-1}>
          {checkInName} logged
        </h1>
        <p className={styles['body']}>
          {count === 1 ? 'One measurement' : `${String(count)} measurements`} at{' '}
          {/* The entry's own zone, not the reader's. An entry recorded abroad
              says the time it was recorded, which is the whole of ADR-0006. */}
          {formatTime(at)}.
        </p>
      </div>

      {/*
        The dots are decoration; the caption beside them is the fact. Said once,
        here — the sentence above deliberately does not repeat it, because a
        screen reader announcing the same run twice is worse than either.
      */}
      {showStreak && (
        <div className={styles['streak']}>
          <ConsistencyStrip
            days={days}
            caption={`${String(streak)} days in a row`}
            variant="dots"
          />
        </div>
      )}

      <div className={styles['actions']}>
        {onSeeChart !== null && (
          <button type="button" className={styles['primary']} onClick={onSeeChart}>
            See it on the chart
          </button>
        )}
        <button type="button" className={styles['secondary']} onClick={onHome}>
          Back to home
        </button>
      </div>
    </div>
  )
}
